/**
 * RewriteDialog — 5-step AI rewrite wizard for the chapter editor.
 *
 * Steps:
 *  1. AI Setup       — model + prompt-caching choice
 *  2. Context        — which project docs to load
 *  3. Structure      — word count, chapter count, 3/5-part story
 *  4. Story Arc      — which arc elements + 6 key attributes
 *  5. Plot Points    — 7 plot points (AI-extracted, editable)
 *  → Confirm + Run
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Brain, BookOpen, SlidersHorizontal, TrendingUp,
  MapPin, ChevronRight, ChevronLeft, Loader2, Check,
  Sparkles, Zap, RefreshCw, BookMarked, FileText,
  Globe, Users, ClipboardList,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AiLoadingOverlay } from '../ui/AiLoadingOverlay';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// ── Model options ─────────────────────────────────────────────────────────────
const LLM_MODELS = [
  {
    provider: 'anthropic', model: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5',
    badge: 'Recommended', description: 'Best creative writing quality, deep context window',
  },
  {
    provider: 'anthropic', model: 'claude-opus-5', label: 'Claude Opus 5',
    badge: 'Powerful', description: 'Maximum literary depth — slower & higher cost',
  },
  {
    provider: 'openai', model: 'gpt-5.6-terra', label: 'GPT-5.6 Terra',
    badge: 'Fast', description: 'Strong narrative coherence, rapid iteration',
  },
  {
    provider: 'gemini', model: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro',
    badge: 'Versatile', description: 'Good all-rounder with large context',
  },
];

// ── Step metadata ─────────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, icon: Brain,           label: 'AI Setup'     },
  { id: 2, icon: BookOpen,        label: 'Context'      },
  { id: 3, icon: SlidersHorizontal, label: 'Structure'  },
  { id: 4, icon: TrendingUp,      label: 'Story Arc'    },
  { id: 5, icon: MapPin,          label: 'Plot Points'  },
];

// ── Story arc toggle row ──────────────────────────────────────────────────────
function ArcToggle({ label, hint, checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border
                      px-4 py-3 hover:bg-accent/40 transition-colors has-[:checked]:border-primary/40
                      has-[:checked]:bg-primary/5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <div className={`mt-0.5 h-4 w-4 shrink-0 rounded border-2 flex items-center justify-center
                       ${checked ? 'border-primary bg-primary' : 'border-border'}`}>
        {checked && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
      </div>
      <div>
        <p className="text-[13px] font-semibold leading-tight">{label}</p>
        {hint && <p className="mt-0.5 text-[11px] text-muted-foreground leading-snug">{hint}</p>}
      </div>
    </label>
  );
}

// ── Plot point editable card ──────────────────────────────────────────────────
function PlotPointCard({ id, label, description, value, instruction, onChange }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-2"
         data-testid={`plot-point-card-${id}`}>
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-primary">{label}</span>
      </div>
      <p className="text-[11.5px] text-muted-foreground leading-snug">{description}</p>
      <Textarea
        rows={2}
        placeholder="Extracted by AI — edit as needed…"
        value={value || ''}
        onChange={(e) => onChange({ value: e.target.value, instruction })}
        className="text-[12.5px] resize-none"
        data-testid={`plot-point-value-${id}`}
      />
      <Input
        placeholder="Optional: instruction for changes (e.g. 'Move this to the midpoint')"
        value={instruction || ''}
        onChange={(e) => onChange({ value, instruction: e.target.value })}
        className="text-[12px] h-8"
        data-testid={`plot-point-instruction-${id}`}
      />
    </div>
  );
}

const PLOT_POINT_DEFS = [
  { id: 'hook',              label: '1. Hook',            description: 'The starting state of the protagonist — often the exact opposite of who they will become.' },
  { id: 'inciting_incident', label: '2. Plot Turn 1 (Inciting Incident)', description: 'An event or choice that disrupts the protagonist\'s normal world and forces them onto their adventure.' },
  { id: 'pinch_point_1',    label: '3. Pinch Point 1',   description: 'Introduction/action of the antagonist that applies pressure and raises the stakes.' },
  { id: 'midpoint',          label: '4. Midpoint',        description: 'The exact centre where the protagonist stops reacting and takes active control.' },
  { id: 'pinch_point_2',    label: '5. Pinch Point 2',   description: 'Intense pressure — plans fail, defeat seems nearly certain ("all is lost").' },
  { id: 'plot_turn_2',      label: '6. Plot Turn 2',     description: 'The protagonist discovers the final piece of information or inner strength needed to win.' },
  { id: 'resolution',        label: '7. Resolution',      description: 'The final climax and conclusion where the protagonist resolves the main conflict and completes their transformation.' },
];

// ─────────────────────────────────────────────────────────────────────────────
export function RewriteDialog({ open, onOpenChange, projectId, projectTitle }) {
  const [step, setStep] = useState(1);

  // Step 1 — AI Setup
  const [selectedModel, setSelectedModel] = useState(LLM_MODELS[0]);
  const [promptCaching, setPromptCaching] = useState(true);

  // Step 2 — Context
  const [includeSynopsis,   setIncludeSynopsis]   = useState(true);
  const [includeStoryBible, setIncludeStoryBible] = useState(true);
  const [includeChapters,   setIncludeChapters]   = useState(true);

  // Step 3 — Structure
  const [targetWordCount,    setTargetWordCount]    = useState('');
  const [targetChapterCount, setTargetChapterCount] = useState(0);
  const [storyStructure,     setStoryStructure]     = useState('5_part');

  // Step 4 — Story Arc
  const [arc, setArc] = useState({
    exposition: true, rising_action: true, climax: true,
    falling_action: true, resolution: true,
  });
  const [attrs, setAttrs] = useState({
    cause_effect: false, central_conflict: false, character_agency: false,
    raising_stakes: false, pacing: false, subplots: false,
  });

  // Step 5 — Plot Points
  const [plotPoints, setPlotPoints] = useState(() =>
    PLOT_POINT_DEFS.reduce((a, d) => ({ ...a, [d.id]: { value: '', instruction: '' } }), {})
  );
  const [extracting, setExtracting] = useState(false);
  const [extracted,  setExtracted]  = useState(false);

  // Run state
  const [running,  setRunning]  = useState(false);
  const [jobId,    setJobId]    = useState(null);
  const [jobDone,  setJobDone]  = useState(false);
  const [jobError, setJobError] = useState(null);
  const pollRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1); setExtracted(false); setJobId(null);
      setJobDone(false); setJobError(null); setRunning(false);
    }
  }, [open]);

  // ── Extract plot points ──────────────────────────────────────────────────
  const handleExtract = useCallback(async () => {
    if (!projectId) return;
    setExtracting(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/rewrite/extract-plot-points`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          llm_model: selectedModel.model,
          llm_provider: selectedModel.provider,
          prompt_caching: promptCaching,
          include_synopsis: includeSynopsis,
          include_story_bible: includeStoryBible,
          include_chapters: includeChapters,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Extraction failed');
      const pp = data.plot_points || {};
      setPlotPoints((prev) => {
        const next = { ...prev };
        PLOT_POINT_DEFS.forEach(({ id }) => {
          if (pp[id]) next[id] = { value: pp[id], instruction: '' };
        });
        // Also store the arc summaries for display
        ['exposition_summary','rising_action_summary','climax_summary',
         'falling_action_summary','resolution_summary'].forEach((k) => {
          if (pp[k]) next[k] = { value: pp[k], instruction: '' };
        });
        return next;
      });
      setExtracted(true);
      toast.success('Plot points extracted by AI');
    } catch (err) {
      toast.error('Extraction failed: ' + err.message);
    } finally {
      setExtracting(false);
    }
  }, [projectId, selectedModel, promptCaching, includeSynopsis, includeStoryBible, includeChapters]);

  // ── Start rewrite job ────────────────────────────────────────────────────
  const handleRun = useCallback(async () => {
    if (!projectId) return;
    setRunning(true); setJobError(null);
    try {
      const res = await fetch(`${API}/projects/${projectId}/rewrite/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          llm_model:       selectedModel.model,
          llm_provider:    selectedModel.provider,
          prompt_caching:  promptCaching,
          target_word_count:    targetWordCount ? parseInt(targetWordCount, 10) : null,
          target_chapter_count: targetChapterCount,
          story_structure:      storyStructure,
          work_on_exposition:    arc.exposition,
          work_on_rising_action: arc.rising_action,
          work_on_climax:        arc.climax,
          work_on_falling_action: arc.falling_action,
          work_on_resolution:    arc.resolution,
          adjust_cause_effect:     attrs.cause_effect,
          adjust_central_conflict: attrs.central_conflict,
          adjust_character_agency: attrs.character_agency,
          adjust_raising_stakes:   attrs.raising_stakes,
          adjust_pacing:           attrs.pacing,
          adjust_subplots:         attrs.subplots,
          plot_points:  plotPoints,
          include_synopsis:    includeSynopsis,
          include_story_bible: includeStoryBible,
          include_chapters:    includeChapters,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start');
      setJobId(data.job_id);
      toast.success('Rewrite job started');
      // Begin polling
      pollRef.current = setInterval(async () => {
        const sr = await fetch(`${API}/projects/${projectId}/rewrite/${data.job_id}/status`, {
          credentials: 'include',
        });
        const sd = await sr.json();
        if (sd.status === 'done') {
          clearInterval(pollRef.current);
          setJobDone(true); setRunning(false);
          toast.success('Rewrite plan complete — check the project notes');
        } else if (sd.status === 'error') {
          clearInterval(pollRef.current);
          setJobError(sd.error || 'Unknown error');
          setRunning(false);
        }
      }, 3000);
    } catch (err) {
      toast.error('Start failed: ' + err.message);
      setRunning(false);
    }
  }, [projectId, selectedModel, promptCaching, targetWordCount, targetChapterCount,
      storyStructure, arc, attrs, plotPoints, includeSynopsis, includeStoryBible, includeChapters]);

  // Cleanup poll on unmount
  useEffect(() => () => clearInterval(pollRef.current), []);

  const canNext = step < 5;
  const canPrev = step > 1;

  // ── Step renderers ───────────────────────────────────────────────────────
  const renderStep = () => {
    if (step === 1) return (
      <div className="space-y-4">
        <p className="text-[12.5px] text-muted-foreground">
          Choose the AI model that will read your manuscript and generate the rewrite plan.
          Prompt caching reuses the loaded context across calls to save cost.
        </p>
        <div className="grid gap-3">
          {LLM_MODELS.map((m) => (
            <label
              key={m.model}
              className={`flex cursor-pointer items-start gap-4 rounded-xl border-2 p-4
                         transition-all ${selectedModel.model === m.model
                           ? 'border-primary bg-primary/5'
                           : 'border-border hover:border-primary/30'}`}
              data-testid={`rewrite-model-${m.model}`}
            >
              <input type="radio" name="llm_model" className="sr-only"
                     checked={selectedModel.model === m.model}
                     onChange={() => setSelectedModel(m)} />
              <div className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center
                               ${selectedModel.model === m.model
                                 ? 'border-primary' : 'border-muted-foreground'}`}>
                {selectedModel.model === m.model && (
                  <div className="h-2 w-2 rounded-full bg-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-semibold">{m.label}</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{m.badge}</Badge>
                </div>
                <p className="mt-0.5 text-[11.5px] text-muted-foreground">{m.description}</p>
              </div>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
          <div>
            <p className="text-[13px] font-semibold flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-amber-500" />
              Prompt Caching
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Cache your manuscript context — reduces cost on multi-step rewrites
            </p>
          </div>
          <Switch
            checked={promptCaching}
            onCheckedChange={setPromptCaching}
            data-testid="rewrite-prompt-caching"
          />
        </div>
      </div>
    );

    if (step === 2) return (
      <div className="space-y-4">
        <p className="text-[12.5px] text-muted-foreground">
          Select which project documents the AI should read when generating the rewrite plan.
          More context = better recommendations.
        </p>
        {[
          { id: 'synopsis', label: 'Synopsis / Plot Summary', icon: FileText,
            hint: 'The overall story arc and premise', checked: includeSynopsis, set: setIncludeSynopsis },
          { id: 'story_bible', label: 'Story Bible & World-Builder Notes', icon: Globe,
            hint: 'Tone, mood, world rules, themes', checked: includeStoryBible, set: setIncludeStoryBible },
          { id: 'chapters', label: 'First 3 Chapters (sample prose)', icon: BookMarked,
            hint: 'Lets AI assess your actual voice and pacing', checked: includeChapters, set: setIncludeChapters },
        ].map(({ id, label, icon: Icon, hint, checked, set }) => (
          <label key={id}
                 className={`flex cursor-pointer items-start gap-4 rounded-xl border-2 p-4
                              transition-all ${checked ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/20'}`}>
            <input type="checkbox" className="sr-only"
                   checked={checked} onChange={(e) => set(e.target.checked)} />
            <div className={`mt-0.5 h-4 w-4 shrink-0 rounded border-2 flex items-center justify-center
                             ${checked ? 'border-primary bg-primary' : 'border-border'}`}>
              {checked && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
            </div>
            <Icon className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-[13px] font-semibold">{label}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>
            </div>
          </label>
        ))}
      </div>
    );

    if (step === 3) return (
      <div className="space-y-6">
        <p className="text-[12.5px] text-muted-foreground">
          Set your structural targets. Leave word count blank to keep the current length.
        </p>
        {/* Word count */}
        <div className="space-y-2">
          <Label className="text-[13px] font-semibold">Target Prose Word Count</Label>
          <Input
            type="number"
            placeholder="e.g. 80000  (leave blank to keep current)"
            value={targetWordCount}
            onChange={(e) => setTargetWordCount(e.target.value)}
            min={1000} max={500000}
            className="w-full"
            data-testid="rewrite-word-count"
          />
        </div>

        {/* Chapter count */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-[13px] font-semibold">Target Chapter Count</Label>
            <span className="text-[13px] font-mono text-primary">
              {targetChapterCount === 0 ? 'Keep current' : targetChapterCount}
            </span>
          </div>
          <Slider
            min={0} max={50} step={1}
            value={[targetChapterCount]}
            onValueChange={([v]) => setTargetChapterCount(v)}
            data-testid="rewrite-chapter-count"
          />
          <p className="text-[11px] text-muted-foreground">
            0 = keep current structure · max 50 chapters
          </p>
        </div>

        {/* Story structure */}
        <div className="space-y-2">
          <Label className="text-[13px] font-semibold">Narrative Structure</Label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: '3_part', label: '3-Part', desc: 'Act 1 / Act 2 / Act 3' },
              { key: '5_part', label: '5-Part', desc: 'Expo · Rising · Climax · Falling · Resolution' },
            ].map(({ key, label, desc }) => (
              <label key={key}
                     className={`flex flex-col cursor-pointer items-center gap-1 rounded-xl border-2 p-4
                                  transition-all ${storyStructure === key
                                    ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/20'}`}
                     data-testid={`rewrite-structure-${key}`}>
                <input type="radio" name="story_structure" className="sr-only"
                       checked={storyStructure === key}
                       onChange={() => setStoryStructure(key)} />
                <span className="text-[14px] font-bold">{label}</span>
                <span className="text-[10.5px] text-muted-foreground text-center">{desc}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    );

    if (step === 4) return (
      <div className="space-y-5 overflow-y-auto max-h-[440px] pr-1">
        <p className="text-[12.5px] text-muted-foreground">
          Select the narrative elements to strengthen in the rewrite, and the key attributes to adjust.
        </p>

        {/* Arc elements */}
        <div>
          <p className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
            Story Arc Elements to Work On
          </p>
          <div className="grid gap-2">
            <ArcToggle label="Exposition" checked={arc.exposition} onChange={(v) => setArc((a) => ({ ...a, exposition: v }))}
              hint="Status quo, main characters introduced, baseline reality established." />
            <ArcToggle label="Rising Action" checked={arc.rising_action} onChange={(v) => setArc((a) => ({ ...a, rising_action: v }))}
              hint="Inciting incident, escalating conflicts and obstacles testing the protagonist." />
            <ArcToggle label="Climax" checked={arc.climax} onChange={(v) => setArc((a) => ({ ...a, climax: v }))}
              hint="Peak tension — protagonist faces the core conflict, decisive turning point." />
            <ArcToggle label="Falling Action" checked={arc.falling_action} onChange={(v) => setArc((a) => ({ ...a, falling_action: v }))}
              hint="Immediate aftermath — consequences play out, secondary tensions settle." />
            <ArcToggle label="Resolution (Denouement)" checked={arc.resolution} onChange={(v) => setArc((a) => ({ ...a, resolution: v }))}
              hint="Final state — loose ends tied up, new normal established." />
          </div>
        </div>

        {/* Key attributes */}
        <div>
          <p className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
            Key Plot Attribute Adjustments
          </p>
          <div className="grid gap-2">
            <ArcToggle label="Cause-and-Effect Logic" checked={attrs.cause_effect}
              onChange={(v) => setAttrs((a) => ({ ...a, cause_effect: v }))}
              hint="Ensure every major action logically triggers the next event — no 'and then' plotting." />
            <ArcToggle label="Central Conflict" checked={attrs.central_conflict}
              onChange={(v) => setAttrs((a) => ({ ...a, central_conflict: v }))}
              hint="Sharpen the internal, external or interpersonal struggle the protagonist must solve." />
            <ArcToggle label="Character Agency" checked={attrs.character_agency}
              onChange={(v) => setAttrs((a) => ({ ...a, character_agency: v }))}
              hint="Protagonist's choices and flaws should actively shape the plot, not passive events." />
            <ArcToggle label="Raising Stakes" checked={attrs.raising_stakes}
              onChange={(v) => setAttrs((a) => ({ ...a, raising_stakes: v }))}
              hint="Make consequences grow heavier — failure must feel increasingly costly." />
            <ArcToggle label="Pacing & Momentum" checked={attrs.pacing}
              onChange={(v) => setAttrs((a) => ({ ...a, pacing: v }))}
              hint="Balance high-energy scenes with quieter characterisation moments." />
            <ArcToggle label="Subplots" checked={attrs.subplots}
              onChange={(v) => setAttrs((a) => ({ ...a, subplots: v }))}
              hint="Secondary storylines that deepen theme and converge by the end." />
          </div>
        </div>
      </div>
    );

    if (step === 5) return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-[12.5px] text-muted-foreground flex-1">
            The AI extracts these from your manuscript. Edit any value, and add an optional
            instruction for each change you want made in the rewrite.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExtract}
            disabled={extracting}
            className="ml-3 shrink-0 h-8 text-[12px] gap-1.5"
            data-testid="rewrite-extract-btn"
          >
            {extracting
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Sparkles className="h-3.5 w-3.5" />}
            {extracted ? 'Re-extract' : 'Extract from manuscript'}
          </Button>
        </div>
        <div className="max-h-[400px] overflow-y-auto space-y-3 pr-1">
          {PLOT_POINT_DEFS.map((def) => (
            <PlotPointCard
              key={def.id}
              id={def.id}
              label={def.label}
              description={def.description}
              value={plotPoints[def.id]?.value}
              instruction={plotPoints[def.id]?.instruction}
              onChange={(v) => setPlotPoints((pp) => ({ ...pp, [def.id]: v }))}
            />
          ))}
        </div>
      </div>
    );

    return null;
  };

  // ── Confirmation / Run panel ─────────────────────────────────────────────
  const renderRunPanel = () => (
    <div className="mt-5 rounded-xl border border-primary/20 bg-primary/5 p-5 space-y-4">
      <div className="flex items-start gap-3">
        <RefreshCw className={`h-5 w-5 text-primary mt-0.5 ${running ? 'animate-spin' : ''}`} />
        <div>
          <p className="text-[14px] font-semibold">
            {jobDone
              ? 'Rewrite plan is ready!'
              : jobError
                ? 'Rewrite failed'
                : running
                  ? 'Generating rewrite plan…'
                  : 'Ready to run rewrite'}
          </p>
          <p className="text-[11.5px] text-muted-foreground mt-0.5">
            {jobDone
              ? 'Your chapter-by-chapter rewrite plan has been saved to the project. Open the project notes to review.'
              : jobError
                ? jobError
                : running
                  ? `Using ${selectedModel.label} · Job ${jobId}`
                  : `${selectedModel.label} will analyse your manuscript and produce a chapter-by-chapter rewrite plan.`}
          </p>
        </div>
      </div>
      {!running && !jobDone && !jobError && (
        <Button
          onClick={handleRun}
          className="w-full gap-2"
          data-testid="rewrite-run-btn"
        >
          <Sparkles className="h-4 w-4" />
          Generate Rewrite Plan
        </Button>
      )}
      {jobDone && (
        <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
          Close & Review
        </Button>
      )}
      {jobError && (
        <Button variant="outline" onClick={() => { setJobError(null); setRunning(false); }} className="w-full">
          Try Again
        </Button>
      )}
    </div>
  );

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0"
        data-testid="rewrite-dialog"
      >
        <DialogHeader className="shrink-0 px-6 pt-6 pb-4 border-b border-border">
          <DialogTitle className="flex items-center gap-2.5 text-[16px]">
            <RefreshCw className="h-5 w-5 text-primary" />
            Novel Rewrite Dialogue
            {projectTitle && (
              <span className="ml-1 text-[13px] font-normal text-muted-foreground">
                — {projectTitle}
              </span>
            )}
          </DialogTitle>

          {/* Step progress bar */}
          <div className="mt-4 flex items-center gap-1.5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = step === s.id;
              const done   = step > s.id;
              return (
                <React.Fragment key={s.id}>
                  <button
                    type="button"
                    onClick={() => done && setStep(s.id)}
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold
                                transition-all
                                ${active  ? 'bg-primary text-primary-foreground'
                                : done    ? 'bg-primary/20 text-primary hover:bg-primary/30 cursor-pointer'
                                          : 'bg-muted text-muted-foreground cursor-not-allowed'}`}
                    disabled={!done && !active}
                    data-testid={`rewrite-step-${s.id}`}
                  >
                    {done
                      ? <Check className="h-3 w-3" />
                      : <Icon className="h-3 w-3" />}
                    <span className="hidden sm:inline">{s.label}</span>
                  </button>
                  {i < STEPS.length - 1 && (
                    <div className={`h-px flex-1 rounded ${step > s.id ? 'bg-primary/40' : 'bg-border'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </DialogHeader>

        {/* Step content — overlay shown while AI runs */}
        <div className="relative flex-1 overflow-y-auto px-6 py-5">
          {renderStep()}
          {step === 5 && renderRunPanel()}
          <AiLoadingOverlay
            visible={extracting || running}
            message={extracting ? 'Extracting 7 plot points with Claude…' : 'Generating rewrite plan with AI…'}
          />
        </div>

        {/* Footer navigation */}
        <div className="shrink-0 px-6 py-4 border-t border-border flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStep((s) => s - 1)}
            disabled={!canPrev}
            className="gap-1.5"
            data-testid="rewrite-prev-btn"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>

          <span className="text-[11px] text-muted-foreground">Step {step} of {STEPS.length}</span>

          {canNext ? (
            <Button
              size="sm"
              onClick={() => setStep((s) => s + 1)}
              className="gap-1.5"
              data-testid="rewrite-next-btn"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <div className="w-16" /> // spacer to keep layout balanced on step 5
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
